// Pure persona-editor draft + spec round-trip helpers. The revise path
// preserves the existing spec and edits the authored voice behavior controls;
// `llm_provider`, `voice_mapping`, and `phone_endpoint` are written from the
// form. Provider/model/voice choices come exclusively from the
// `persona_provider_policy` projection on GET /v1/me (#1441) — this module
// carries no provider, model, or voice literals of its own.
// No React, no network.
import type { components } from "../../api/schema";
import { isRecord } from "../agent/lib/connectionConfig";
import { unicodeCaseFold } from "../../lib/unicodeCaseFold";

export type PersonaVariableValues = Record<string, unknown>;

type FirstSpeaker = components["schemas"]["FirstSpeaker"];
type PersonaLLMProvider = components["schemas"]["PersonaLLMProvider"];
type PersonaPhoneEndpoint = components["schemas"]["PersonaPhoneEndpoint"];
type PersonaPhoneEndpointMode =
  components["schemas"]["PersonaPhoneEndpointMode"];
type PersonaProviderPolicy =
  components["schemas"]["PersonaProviderPolicyResponse"];
type PersonaProvider = components["schemas"]["PersonaProviderResponse"];
type PersonaResponse = components["schemas"]["PersonaResponse"];
type PersonaSpec = components["schemas"]["PersonaSpec"];
type PersonaVoiceMapping = components["schemas"]["PersonaVoiceMapping"];
type PersonaVoiceOption = components["schemas"]["PersonaVoiceOptionResponse"];
type PersonaVoiceProvider = components["schemas"]["PersonaVoiceProvider"];

// Fail-closed policy while /v1/me is pending or unavailable: nothing is
// offered, and save is blocked with a clear message.
export const unavailablePersonaProviders: PersonaProviderPolicy = {
  llm_providers: [],
  voice_providers: [],
  // Offers nothing, but asserts nothing permanent either: only a served
  // `voice_supported: false` proves the deployment can never do voice, so
  // this transient fallback must not claim it.
  voice_supported: true,
};

export interface PersonaFormDraft {
  // The owning agent, chosen in the create form when the page is unscoped;
  // pre-filled (and not rendered) when the page carries an agent scope.
  agentId: string;
  displayName: string;
  // provider_id tokens from persona_provider_policy; "" until chosen (or
  // when the deployment enables none).
  llmProvider: string;
  mode: PersonaPhoneEndpointMode;
  poolNumberRef: string;
  voiceProvider: string;
  voiceRef: string;
  firstSpeaker: FirstSpeaker;
  openingLine: string;
  bargeInMinWords: string;
}

export interface PersonaFormSubmission {
  agentId: string;
  displayName: string;
  llmProvider: PersonaLLMProvider;
  phoneEndpoint: PersonaPhoneEndpoint;
  firstSpeaker: FirstSpeaker;
  openingLine: string;
  bargeInMinWords: number | null;
  // null when the deployment has no voice providers: the form omits the
  // voice section and the save leaves any existing voice_mapping untouched.
  voiceMapping: PersonaVoiceMapping | null;
  variableValues?: PersonaVariableValues;
}

export type PersonaSubmissionResult =
  | { error: string; ok: false }
  | { ok: true; value: PersonaFormSubmission };

export function buildPersonaSubmission(
  draft: PersonaFormDraft,
  policy: PersonaProviderPolicy,
  variableValues: PersonaVariableValues = {},
): PersonaSubmissionResult {
  const agentId = draft.agentId.trim();
  if (agentId.length === 0) {
    return { error: "Agent is required.", ok: false };
  }
  const displayName = draft.displayName.trim();
  if (displayName.length === 0) {
    return { error: "Display name is required.", ok: false };
  }
  if (policy.llm_providers.length === 0) {
    return {
      error: "No LLM provider is configured. Ask your administrator.",
      ok: false,
    };
  }
  const llmProvider = providerById(policy.llm_providers, draft.llmProvider);
  if (llmProvider === undefined) {
    return { error: "LLM provider is required.", ok: false };
  }
  const bargeInMinWords =
    draft.bargeInMinWords.trim() === "" ? null : Number(draft.bargeInMinWords);
  if (
    bargeInMinWords !== null &&
    (!Number.isInteger(bargeInMinWords) ||
      bargeInMinWords < 1 ||
      bargeInMinWords > 8)
  ) {
    return { error: "Barge-in threshold must be 1–8 whole words.", ok: false };
  }
  const selection = {
    agentId,
    displayName,
    firstSpeaker: draft.firstSpeaker,
    openingLine: draft.openingLine,
    bargeInMinWords,
    // provider_id values in the policy are exactly the sealed enum tokens
    // (server-enforced parity, #1441); the server re-validates on save.
    llmProvider: llmProvider.provider_id as PersonaLLMProvider,
  };
  let voiceMapping: PersonaVoiceMapping | null = null;
  if (policy.voice_providers.length > 0) {
    // Founder ruling (#1441): voice is required whenever the deployment has
    // at least one voice provider.
    const voiceProvider = providerById(
      policy.voice_providers,
      draft.voiceProvider,
    );
    if (voiceProvider === undefined) {
      return { error: "Voice provider is required.", ok: false };
    }
    const voiceRef = draft.voiceRef.trim();
    if (voiceRef.length === 0) {
      return { error: "Voice is required.", ok: false };
    }
    // Catalog-only selection (founder ruling 2026-07-31): the server rejects
    // any voice_ref outside the deployment's catalog, so the form refuses it
    // first instead of round-tripping a save that cannot succeed.
    if (voiceProvider.voices.length === 0) {
      return {
        error:
          "No voice is configured for this provider. Ask your administrator.",
        ok: false,
      };
    }
    if (!voiceProvider.voices.some((voice) => voice.voice_ref === voiceRef)) {
      return {
        error: "Select a voice available in this deployment.",
        ok: false,
      };
    }
    voiceMapping = personaVoiceMapping(
      voiceProvider.provider_id as PersonaVoiceProvider,
      voiceRef,
    );
  }
  const withVoice = { ...selection, voiceMapping };
  if (draft.mode === "pinned") {
    const poolNumberRef = draft.poolNumberRef.trim();
    if (poolNumberRef.length === 0) {
      return { error: "Phone number is required.", ok: false };
    }
    return {
      ok: true,
      value: {
        ...withVoice,
        phoneEndpoint: {
          mode: "pinned",
          pool_number_ref: poolNumberRef,
        },
        ...(Object.keys(variableValues).length > 0 ? { variableValues } : {}),
      },
    };
  }
  return {
    ok: true,
    value: {
      ...withVoice,
      phoneEndpoint: { mode: "auto", pool_number_ref: null },
      ...(Object.keys(variableValues).length > 0 ? { variableValues } : {}),
    },
  };
}

export function providerById(
  providers: readonly PersonaProvider[],
  providerId: string,
): PersonaProvider | undefined {
  return providers.find((provider) => provider.provider_id === providerId);
}

// Display label for a provider token: policy display_name when the provider
// is enabled here, else the bare token (a persona saved under a different
// deployment shape stays legible, never relabeled client-side).
export function providerLabel(
  providers: readonly PersonaProvider[],
  providerId: string,
): string {
  return providerById(providers, providerId)?.display_name ?? providerId;
}

// The voice the form pre-selects for a provider: its declared default when
// the catalog still carries it, else its first catalog voice, else empty (a
// provider with no declared voices has nothing to select).
export function defaultVoiceRefFor(provider: PersonaProvider): string {
  const declared = provider.default_voice_ref;
  if (
    declared != null &&
    provider.voices.some((voice) => voice.voice_ref === declared)
  ) {
    return declared;
  }
  return provider.voices[0]?.voice_ref ?? "";
}

// One voice's row label: the human name, its vendor family, and the spoken
// locale — never the provider-native ref, which is an id, not a name.
export function voiceOptionLabel(voice: PersonaVoiceOption): string {
  const locale = voiceLocaleLabel(voice.locale);
  return locale === null
    ? `${voice.display_name} · ${voice.family}`
    : `${voice.display_name} · ${voice.family} · ${locale}`;
}

// The saved voice rendered for a persona row: the catalog display label, or
// null when the deployment no longer offers that ref (the caller says so
// rather than printing a raw id).
export function voiceLabel(
  providers: readonly PersonaProvider[],
  providerId: string,
  voiceRef: string,
): string | null {
  const voice = providerById(providers, providerId)?.voices.find(
    (option) => option.voice_ref === voiceRef,
  );
  return voice === undefined ? null : voiceOptionLabel(voice);
}

// Locale rendered in the viewer's own language via Intl, plus a region flag
// only when the locale actually names a region — a bare "en" is not any one
// country and gets no flag. No per-voice flag data is carried anywhere.
export function voiceLocaleLabel(locale: string | null): string | null {
  if (locale === null || locale.trim().length === 0) {
    return null;
  }
  const tag = locale.trim();
  const name = languageDisplayName(tag);
  const flag = regionFlag(tag);
  return flag === null ? name : `${flag} ${name}`;
}

function languageDisplayName(tag: string): string {
  try {
    const display = new Intl.DisplayNames(undefined, { type: "language" });
    return display.of(tag) ?? tag;
  } catch {
    return tag;
  }
}

function regionFlag(tag: string): string | null {
  const region = tag.split(/[-_]/)[1];
  if (region === undefined || !/^[A-Za-z]{2}$/.test(region)) {
    return null;
  }
  // Two ASCII letters by construction (the regex above), so charCodeAt is
  // exact here: each maps to its regional-indicator code point.
  const upper = region.toUpperCase();
  const base = 0x1f1e6 - "A".charCodeAt(0);
  return String.fromCodePoint(
    upper.charCodeAt(0) + base,
    upper.charCodeAt(1) + base,
  );
}

// Read-only model line for the selected LLM provider: the default model's
// display name (honest about "always adapter default" today; a per-persona
// model picker needs docs/open-questions.md#persona-llm-model-selection).
export function defaultModelLabel(provider: PersonaProvider): string | null {
  const ref = provider.default_model_ref;
  if (ref == null) {
    return null;
  }
  const option = provider.models.find((model) => model.model_ref === ref);
  return option?.display_name ?? ref;
}

// Editor draft: from the edit subject when present, else the create-mode
// defaults (first enabled provider + its default voice — server-projected,
// nothing hard-coded). `scopedAgentId` pre-fills the owning agent when the
// page carries an agent scope.
export function personaDraftFromForm(
  persona: PersonaResponse | null,
  policy: PersonaProviderPolicy,
  scopedAgentId?: string,
): PersonaFormDraft {
  if (persona) {
    const endpoint = personaPhoneEndpoint(persona);
    const voice = personaVoiceSelection(persona);
    const voiceProvider =
      voice === null
        ? (policy.voice_providers[0]?.provider_id ?? "")
        : voice.provider;
    const voiceRef =
      voice === null
        ? defaultDraftVoiceRef(policy, voiceProvider)
        : voice.voiceRef;
    return {
      firstSpeaker: personaFirstSpeaker(persona),
      openingLine: personaOpeningLine(persona),
      bargeInMinWords: personaBargeInMinWords(persona),
      agentId: persona.agent_id,
      displayName: persona.display_name,
      llmProvider: personaLlmProvider(persona),
      mode: endpoint.mode,
      poolNumberRef: endpoint.pool_number_ref ?? "",
      voiceProvider,
      voiceRef,
    };
  }
  const voiceProvider = policy.voice_providers[0]?.provider_id ?? "";
  return {
    firstSpeaker: "agent",
    openingLine: "",
    bargeInMinWords: "",
    agentId: scopedAgentId ?? "",
    displayName: "",
    llmProvider: policy.llm_providers[0]?.provider_id ?? "",
    mode: "auto",
    poolNumberRef: "",
    voiceProvider,
    voiceRef: defaultDraftVoiceRef(policy, voiceProvider),
  };
}

function defaultDraftVoiceRef(
  policy: PersonaProviderPolicy,
  voiceProviderId: string,
): string {
  const provider = providerById(policy.voice_providers, voiceProviderId);
  return provider === undefined ? "" : defaultVoiceRefFor(provider);
}

// Order-insensitive spec equality: an edit save skips the `:revise` call (no
// noise revision, no noise audit row) when the merged spec matches the
// current revision byte-for-byte after key ordering is normalized.
export function personaSpecEquals(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  if (isRecord(value)) {
    const entries = Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`);
    return `{${entries.join(",")}}`;
  }
  if (value === undefined) {
    return "null";
  }
  return JSON.stringify(value);
}

// Revise (edit) preserves the subject's existing spec; create writes a fresh
// one.
export function personaSpecForSave(
  persona: PersonaResponse | null,
  submission: PersonaFormSubmission,
): PersonaSpec {
  if (persona) {
    return personaSpecWithSelections(submission, persona.current_revision.spec);
  }
  return personaSpecWithSelections(submission);
}

export function personaSpecWithSelections(
  submission: PersonaFormSubmission,
  existingSpec: PersonaResponse["current_revision"]["spec"] = null,
): PersonaSpec {
  const voiceBehavior = (
    existingSpec && "voice_behavior" in existingSpec
      ? existingSpec.voice_behavior
      : null
  ) as PersonaSpec["voice_behavior"];
  // No voice providers in this deployment: the form has no voice section, so
  // an existing voice_mapping is carried through verbatim, never rewritten.
  const existingVoiceMapping = (
    existingSpec && "voice_mapping" in existingSpec
      ? existingSpec.voice_mapping
      : null
  ) as PersonaSpec["voice_mapping"];
  const variableValues = submission.variableValues ?? {};
  const suppliedKeys = new Set(
    Object.keys(variableValues).map((key) => unicodeCaseFold(key)),
  );
  const preserved = Object.fromEntries(
    Object.entries(existingSpec ?? {}).filter(
      ([key]) => !suppliedKeys.has(unicodeCaseFold(key)),
    ),
  );
  const supplied = Object.fromEntries(
    Object.entries(variableValues).filter(([, value]) => value !== undefined),
  );
  return {
    ...preserved,
    llm_provider: submission.llmProvider,
    phone_endpoint: submission.phoneEndpoint,
    voice_behavior:
      submission.firstSpeaker === (voiceBehavior?.first_speaker ?? "agent") &&
      submission.openingLine === (voiceBehavior?.opening_line ?? "") &&
      submission.bargeInMinWords === (voiceBehavior?.barge_in_min_words ?? null)
        ? voiceBehavior
        : ({
            ...voiceBehavior,
            first_speaker: submission.firstSpeaker,
            opening_line: submission.openingLine || null,
            barge_in_min_words: submission.bargeInMinWords,
          } as PersonaSpec["voice_behavior"]),
    voice_mapping: submission.voiceMapping ?? existingVoiceMapping,
    ...supplied,
  };
}

export function personaVoiceMapping(
  provider: PersonaVoiceProvider,
  voiceRef: string,
): PersonaVoiceMapping {
  return {
    deepgram: provider === "deepgram" ? voiceRef : null,
    elevenlabs: provider === "elevenlabs" ? voiceRef : null,
    google: provider === "google" ? voiceRef : null,
    provider,
  };
}

// Existing personas without turn order wait for the agent.
export function personaFirstSpeaker(persona: PersonaResponse): FirstSpeaker {
  const spec = persona.current_revision.spec;
  const behavior = isRecord(spec) ? spec.voice_behavior : null;
  return isRecord(behavior) && behavior.first_speaker === "persona"
    ? "persona"
    : "agent";
}

function personaBargeInMinWords(persona: PersonaResponse): string {
  const spec = persona.current_revision.spec;
  const behavior = isRecord(spec) ? spec.voice_behavior : null;
  return isRecord(behavior) && typeof behavior.barge_in_min_words === "number"
    ? String(behavior.barge_in_min_words)
    : "";
}

function personaOpeningLine(persona: PersonaResponse): string {
  const spec = persona.current_revision.spec;
  const behavior = isRecord(spec) ? spec.voice_behavior : null;
  return isRecord(behavior) && typeof behavior.opening_line === "string"
    ? behavior.opening_line
    : "";
}

// The saved LLM provider token is preserved; policy validates it on save.
export function personaLlmProvider(persona: PersonaResponse): string {
  const spec = persona.current_revision.spec;
  const provider = isRecord(spec) ? spec.llm_provider : null;
  return typeof provider === "string" ? provider : "";
}

// The persona's saved voice selection, verbatim; null when the spec carries
// no voice_mapping.
export function personaVoiceSelection(persona: PersonaResponse): {
  provider: string;
  voiceRef: string;
} | null {
  const spec = persona.current_revision.spec;
  const mapping =
    isRecord(spec) && isRecord(spec.voice_mapping) ? spec.voice_mapping : null;
  if (mapping === null) {
    return null;
  }
  const provider = mapping.provider;
  if (typeof provider !== "string" || provider.length === 0) {
    return null;
  }
  const selectedVoice = mapping[provider];
  return {
    provider,
    voiceRef: typeof selectedVoice === "string" ? selectedVoice : "",
  };
}

export function personaPhoneEndpoint(
  persona: PersonaResponse,
): PersonaPhoneEndpoint {
  const spec = persona.current_revision.spec;
  const endpoint =
    isRecord(spec) && isRecord(spec.phone_endpoint)
      ? spec.phone_endpoint
      : null;
  if (
    endpoint?.mode === "pinned" &&
    typeof endpoint.pool_number_ref === "string"
  ) {
    return {
      mode: "pinned",
      pool_number_ref: endpoint.pool_number_ref,
    };
  }
  return { mode: "auto", pool_number_ref: null };
}
